import { Field, type FieldAttributes, useFormikContext } from 'formik';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';

import { hasValue } from '@core/utils/equality';

import { LoadingOption } from '../LookupInput/ApiLookupInputBase';
import { InputMultiLookupBase, type InputMultiLookupOption } from './InputMultiLookupBase';

type InputMultiLookupProps<FormikFieldType, OptionValueType> = {
  label?: string;
  name: string;
  options: InputMultiLookupOption<OptionValueType>[];

  /**
   * onMatchChange is a callback that is invoked when the selected options
   * change; if there are no matches, the callback is passed an empty array.
   *
   * Currently, onMatchChange is invoked immediately prior to touching and
   * setting the value of the Formik field, so within the callback, the previous
   * value is still accessible in Formik's state while the new value is passed
   * to the callback.
   */
  onMatchChange: (matches: InputMultiLookupOption<OptionValueType>[]) => void;

  /** Finds the options that correspond to what's in Formik state. */
  formValueMatcher: (
    formValue: FormikFieldType[],
    options: InputMultiLookupOption<OptionValueType>[],
  ) => InputMultiLookupOption<OptionValueType>[];

  /** @default empty */
  placeholder?: string;

  /** @default undefined */
  disabled?: boolean;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  /** if true - the options will be displayed with checkbox */
  withCheckbox?: boolean;

  /** custom render function for the value of the input. */
  renderValue?: (selected: InputMultiLookupOption<OptionValueType>[]) => ReactNode;

  /**
   * Controlled text value shown in the autocomplete input.
   * Use with `onInputChange` for a fully controlled typing experience.
   */
  inputValue?: string;
  /**
   * Callback fired whenever the user types in the input field.
   * Combine with `inputValue` to control the text and trigger server-side searches.
   */
  onInputValueChange?: (value: string) => void;

  /**
   * Use if options are filtered somewhere else, like during API requests.
   * Combine with onInputValueChange and inputValue to get the filter string.
   */
  disableClientSideFiltering?: boolean;
  /** When true, the helper text area is not rendered when there is no error. Default false.
   *
   * This property exists to match the logic used in the figma design and gradually move the UI towards it.
   * */
  omitEmptyHelperText?: boolean;
};

/**
 * A formValueMatcher for the typical multi-lookup case for domain objects,
 * where Formik state contains an array of { id: string }, and the lookup
 * options' values are string IDs.
 */
export const ReferenceValueMatcher = (
  formValue: { id: string }[],
  options: InputMultiLookupOption<string>[],
): InputMultiLookupOption<string>[] => {
  const optionsByValue = new Map(options.map((opt) => [opt.value, opt]));
  return formValue.map((v) => optionsByValue.get(v.id)).filter((opt) => opt !== undefined);
};

/** A formValueMatcher for a Formik field that holds the chosen options' string IDs. */
export const stringIdFormValueMatcher = (
  formValue: string[],
  options: InputMultiLookupOption<string>[],
): InputMultiLookupOption<string>[] => {
  const optionsByValue = new Map(options.map((opt) => [opt.value, opt]));
  return formValue.map((id) => optionsByValue.get(id)).filter(hasValue);
};

/**
 * Converts an array of options to the expected Formik value structure for an
 * array of YupReferences.
 */
export const OptionsToReferences = (
  options: InputMultiLookupOption<string>[],
): {
  id: string | null;
  option: InputMultiLookupOption<string> | null;
}[] => options.map((option) => ({ id: option.value, option }));

/**
 * A Slabstack formik-friendly multi-selection autocomplete.
 *
 * Must be used inside `<Formik><Form>{component}</Form></Formik>`.
 *
 * @example
 * <Formik {your_values_here} >
 * {(formikBag): ReactElement => (
 *   <Form>
 *     <InputMultiLookup
 *       label='Companies'
 *       name='companies'
 *       options={companyOpts}
 *       onMatchChange={(companyIDs: string[]): void => {
 *         // ...
 *       }}
 *       formValueMatcher={ReferenceValueMatcher}
 *     />
 *   </Form>
 * </Formik>
 */
export const InputMultiLookup = <FormikFieldType, OptionValueType>({
  label,
  name,
  options,
  onMatchChange,
  formValueMatcher,
  placeholder,
  disabled,
  tip,
  withCheckbox,
  renderValue,
  onInputValueChange,
  inputValue,
  disableClientSideFiltering,
  omitEmptyHelperText = false,
}: InputMultiLookupProps<FormikFieldType, OptionValueType>): ReactElement => {
  const { getFieldMeta } = useFormikContext();
  const formikValue = getFieldMeta(name).value as FormikFieldType[];

  // Cache of previously selected options so they survive server-side filtering.
  // Keyed by stringified option value for O(1) lookups.
  const selectedCacheRef = useRef<Map<string, InputMultiLookupOption<OptionValueType>>>(new Map());

  const resolveStateOpts = (): InputMultiLookupOption<OptionValueType>[] => {
    // sorted by name
    const cachedValues = [...selectedCacheRef.current.values()];
    const allKnownOptions = [
      ...cachedValues.filter(
        (cached) => !options.some((opt) => String(opt.value) === String(cached.value)),
      ),
      ...options,
    ].sort((a, b) => a.label.localeCompare(b.label));

    return formValueMatcher(formikValue, allKnownOptions);
  };

  const updateCache = (resolved: InputMultiLookupOption<OptionValueType>[]): void => {
    const resolvedKeys = new Set(resolved.map((r) => String(r.value)));
    for (const key of selectedCacheRef.current.keys()) {
      if (!resolvedKeys.has(key)) {
        selectedCacheRef.current.delete(key);
      }
    }
    resolved.forEach((opt) => {
      selectedCacheRef.current.set(String(opt.value), opt);
    });
  };

  const [stateOpts, setStateOpts] = useState<InputMultiLookupOption<OptionValueType>[]>(() => {
    const initial = resolveStateOpts();
    updateCache(initial);
    return initial;
  });

  useEffect(() => {
    const resolved = resolveStateOpts();
    updateCache(resolved);
    setStateOpts(resolved);
  }, [formikValue, options]);

  return (
    <Field id={name} name={name}>
      {({
        field: { onBlur },
        form: { setFieldTouched },
        meta,
      }: FieldAttributes<any>): ReactElement => {
        const loadingOptions = options.length === 1 && options[0] === LoadingOption;
        const hasError = meta.touched === true && meta.error !== undefined;
        let helperText = omitEmptyHelperText ? undefined : ' ';
        if (hasError) {
          helperText = meta.error;
        }
        return (
          <InputMultiLookupBase
            onBlur={onBlur}
            disabled={disabled}
            loading={loadingOptions}
            error={hasError}
            helperText={helperText}
            placeholder={placeholder}
            tip={tip}
            label={label}
            name={name}
            options={options}
            value={stateOpts}
            inputValue={inputValue}
            onInputValueChange={onInputValueChange}
            disableClientSideFiltering={disableClientSideFiltering}
            onChange={(selectedOpts: InputMultiLookupOption<OptionValueType>[]): void => {
              updateCache(selectedOpts);
              onMatchChange(selectedOpts);
              setStateOpts(selectedOpts);
              requestAnimationFrame(() => {
                setFieldTouched(name, true);
              });
            }}
            withCheckbox={withCheckbox}
            renderValue={renderValue}
          />
        );
      }}
    </Field>
  );
};
